import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { Formik, Form, Field } from 'formik';
import styled from 'styled-components';
import {
  Button,
  Modal,
  Heading,
  Overflow,
  Switch,
  Input,
  Select,
  ErrorMsg,
} from 'next/components/ui';
import { asPromised } from 'utils/as-promised';

import { selectAccountMeta } from 'reducers/account/meta';
import { updateAccountMeta } from 'actions/account/meta';

const OverflowText = styled.p`
  font-size: var(--regular);
`;

const FieldsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
`;

const Footer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;

const InformativeText = styled(OverflowText)`
  margin-top: 14px;
  word-break: break-word;
`;

const ThrottleHighlight = styled.strong`
  font-weight: 700;
`;

const CustomSelect = styled(Select)`
  && {
    min-width: 50%;
  }
`;

export function FrequencyLimitsModalManager({
  throttleContentLimit = '',
  throttleContentTimespanMs = 3600000,
  throttleEnabled = false,
  onSave,
  trigger,
}) {
  const [visible, setVisible] = useState(false);

  const timeframeOptions = [
    { label: 'Minute', value: 60000 },
    { label: 'Hour', value: 3600000 },
    { label: 'Day', value: 86400000 },
    { label: 'Week', value: 604800000 },
  ];

  const handleSubmit = (data, { setSubmitting }) => {
    onSave(data)
      .then(() => {
        setVisible(false);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleTriggerClick = () => setVisible(true);

  return (
    <>
      {React.isValidElement(trigger) ? (
        React.cloneElement(trigger, {
          onClick: handleTriggerClick,
        })
      ) : (
        <Button kind="secondary" key="show" onClick={handleTriggerClick}>
          Frequency limit
        </Button>
      )}

      <Modal
        visible={visible}
        key="dialog"
        onClose={() => setVisible(false)}
        size="m"
      >
        <Heading>Frequency limit</Heading>
        <Overflow>
          <OverflowText>
            Limit how often users can see a Flow or NPS survey.
          </OverflowText>
        </Overflow>
        <Formik
          validateOnChange
          initialValues={{
            throttleContentLimit,
            throttleContentTimespanMs,
            throttleEnabled,
          }}
          validationSchema={yup.object().shape({
            throttleContentLimit: yup
              .number()
              .required()
              .positive()
              .integer()
              .max(100000),
          })}
          onSubmit={handleSubmit}
        >
          {({ values, dirty, errors, isSubmitting }) => {
            const currentTimeframe = timeframeOptions
              .find(({ value }) => value === values.throttleContentTimespanMs)
              .label.toLowerCase();
            return (
              <Form>
                <Field name="throttleEnabled">
                  {({ field: { name, value }, form: { setFieldValue } }) => (
                    <Switch
                      checked={value}
                      textOn="On"
                      textOff="Off"
                      onChange={enableFrequency => {
                        setFieldValue(name, enableFrequency);
                      }}
                    />
                  )}
                </Field>
                <FieldsWrapper>
                  <InputWrapper>
                    <Field name="throttleContentLimit">
                      {({
                        field: { name, value },
                        form: { setFieldValue },
                      }) => (
                        <Input
                          id="throttleContentLimit"
                          aria-label="experiences limit"
                          type="number"
                          placeholder="e.g 10"
                          value={value}
                          min="1"
                          error={Boolean(errors.throttleContentLimit)}
                          onChange={({ target: { value: inputValue } }) => {
                            setFieldValue(name, inputValue);
                          }}
                          onBlur={({ target: { valueAsNumber } }) => {
                            if (
                              Number.isNaN(valueAsNumber) ||
                              valueAsNumber > 100000
                            ) {
                              setFieldValue(name, throttleContentLimit);
                            } else {
                              setFieldValue(
                                name,
                                Number.parseInt(valueAsNumber, 10)
                              );
                            }
                          }}
                          disabled={!values.throttleEnabled}
                        />
                      )}
                    </Field>
                    {errors.throttleContentLimit && (
                      <ErrorMsg>Enter values between 1 - 100,000</ErrorMsg>
                    )}
                  </InputWrapper>
                  <Field name="throttleContentTimespanMs">
                    {({ field: { name, value }, form: { setFieldValue } }) => (
                      <CustomSelect
                        id="throttleContentTimespanMs"
                        data-testid="units"
                        aria-label="units of time"
                        isDisabled={!values.throttleEnabled}
                        options={timeframeOptions}
                        onChange={option => setFieldValue(name, option.value)}
                        value={timeframeOptions.find(
                          option => option.value === value
                        )}
                      />
                    )}
                  </Field>
                </FieldsWrapper>
                {values.throttleEnabled && (
                  <InformativeText>
                    Flows and NPS will not be shown to a user if they have seen
                    more than{' '}
                    <ThrottleHighlight>
                      {values.throttleContentLimit || '--'} per{' '}
                      {currentTimeframe}
                    </ThrottleHighlight>
                    . After the {currentTimeframe}, they will show again.
                  </InformativeText>
                )}
                <Footer>
                  <Button
                    disabled={
                      !dirty || errors.throttleContentLimit || isSubmitting
                    }
                    type="submit"
                  >
                    Save
                  </Button>
                </Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
}

FrequencyLimitsModalManager.propTypes = {
  throttleContentLimit: PropTypes.number,
  throttleContentTimespanMs: PropTypes.number,
  throttleEnabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  trigger: PropTypes.element,
};

const mapStateToProps = state => {
  const meta = selectAccountMeta(state);
  return {
    throttleEnabled: meta?.throttleEnabled,
    throttleContentLimit: meta?.throttleContentLimit,
    throttleContentTimespanMs: meta?.throttleContentTimespanMs,
  };
};

const mapDispatchToProps = dispatch => ({
  onSave: changes => asPromised(dispatch, updateAccountMeta(changes)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(FrequencyLimitsModalManager);
